import * as i0 from '@angular/core';
import { Injectable, Inject, RendererStyleFlags2, EventEmitter, Component, ViewChild, Input, Output, HostListener, NgModule } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';

class ReducedMotionService {
    constructor(_document) {
        this._document = _document;
    }
    /**
     * Check if the system has reduced motion.
     * @returns True if the system has reduced motion, false otherwise.
     */
    hasSystemReducedMotion() {
        var _a;
        const { defaultView } = this._document;
        return ((_a = defaultView === null || defaultView === void 0 ? void 0 : defaultView.matchMedia('(prefers-reduced-motion: reduce)').matches) !== null && _a !== void 0 ? _a : false);
    }
}
ReducedMotionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: ReducedMotionService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
ReducedMotionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: ReducedMotionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: ReducedMotionService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class MarqueeDuplicationService {
    constructor(_document) {
        this._document = _document;
    }
    /**
     * Set the marquee component instance.
     * @param marqueeComponent - NGX Fast Marquee component instance.
     */
    setMarqueeComponent(marqueeComponent) {
        this._marqueeComponent = marqueeComponent;
    }
    /**
     * Duplicate the items of the marquee.
     */
    duplicateItems() {
        this._removeDuplicateItems();
        this._createDuplicateItems();
    }
    /**
     * Remove the duplicated items from the marquee.
     */
    _removeDuplicateItems() {
        const { marqueeInnerElement } = this._marqueeComponent;
        marqueeInnerElement
            .querySelectorAll('[aria-hidden="true"]')
            .forEach(function (element) {
            element.remove();
        });
    }
    /**
     * Create the duplicated items for the marquee based on the marquee width,
     * the marquee inner width and the number of current items.
     */
    _createDuplicateItems() {
        const { marqueeInnerElement, renderer } = this._marqueeComponent;
        const numberOfDuplicates = this._getNumberOfDuplicates();
        const marqueeItems = Array.from(marqueeInnerElement.children);
        const marqueeItemsToBeDuplicated = Array.from({ length: numberOfDuplicates }, () => marqueeItems).reduce((accumulator, currentValue) => accumulator.concat(currentValue), []);
        const fragment = this._document.createDocumentFragment();
        for (const marqueeItem of marqueeItemsToBeDuplicated) {
            const marqueeItemClone = marqueeItem.cloneNode(true);
            renderer.setAttribute(marqueeItemClone, 'aria-hidden', 'true');
            fragment.appendChild(marqueeItemClone);
        }
        renderer.appendChild(marqueeInnerElement, fragment);
    }
    /**
     * Return the number of duplicates based on the marquee size and the marquee inner size and the
     * direction input value.
     * - If the the number of duplicates is odd, it will be adjusted to be even (adding 1) because of
     * the animation repetition is translated 50%.
     * - If the marquee inner size is less than the marquee size, need to be adjusted multiplying by 2
     * in order to fill the marquee.
     * @returns The number of duplicates
     */
    _getNumberOfDuplicates() {
        const { direction } = this._marqueeComponent;
        const [marqueeDOMRect, marqueeInnerDOMRect] = this._getBoundedClientRects();
        const marqueeSize = direction === 'left' || direction === 'right'
            ? marqueeDOMRect.width
            : marqueeDOMRect.height;
        const marqueeInnerSize = direction === 'left' || direction === 'right'
            ? marqueeInnerDOMRect.width
            : marqueeInnerDOMRect.height;
        const numberOfDuplicates = Math.ceil(marqueeSize / marqueeInnerSize);
        if (marqueeSize > marqueeInnerSize) {
            const numberOfDuplicatesAdjustedByScreen = 2 * numberOfDuplicates;
            const isNumberOfDuplicatesAdjustedByScreenOdd = numberOfDuplicatesAdjustedByScreen % 2 !== 0;
            return isNumberOfDuplicatesAdjustedByScreenOdd
                ? numberOfDuplicatesAdjustedByScreen
                : numberOfDuplicatesAdjustedByScreen + 1;
        }
        const isNumberOfDuplicatesOdd = numberOfDuplicates % 2 !== 0;
        return isNumberOfDuplicatesOdd
            ? numberOfDuplicates
            : numberOfDuplicates + 1;
    }
    /**
     * Get the marquee and marquee inner DOMRects.
     * @returns The marquee and marquee inner DOMRects
     */
    _getBoundedClientRects() {
        const { marqueeElement, marqueeInnerElement } = this._marqueeComponent;
        return [
            marqueeElement.getBoundingClientRect(),
            marqueeInnerElement.getBoundingClientRect(),
        ];
    }
}
MarqueeDuplicationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: MarqueeDuplicationService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
MarqueeDuplicationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: MarqueeDuplicationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: MarqueeDuplicationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class MarqueeService {
    constructor(_reducedMotionService, _marqueeDuplicationService) {
        this._reducedMotionService = _reducedMotionService;
        this._marqueeDuplicationService = _marqueeDuplicationService;
        this._currentNumberOfMarqueeItems = 0;
    }
    /**
     * Set the marquee component instance.
     * @param marqueeComponent - NGX Fast Marquee component instance.
     */
    setMarqueeComponent(marqueeComponent) {
        this._marqueeComponent = marqueeComponent;
        this._marqueeDuplicationService.setMarqueeComponent(marqueeComponent);
    }
    /**
     * Update the marquee on input changes.
     * @param changes - Input Changes
     */
    updateOnInputChanges(changes) {
        if (!this._marqueeComponent)
            return;
        if (this._hasInputChange(changes, 'direction')) {
            this.updateDirection();
        }
        if (this._hasInputChange(changes, 'speed')) {
            this.updateSpeed();
        }
        if (this._hasInputChange(changes, 'play')) {
            this.updatePlayState();
        }
        if (this._hasInputChange(changes, 'maskPercentage')) {
            this.updateMask();
        }
        if (this._hasInputChange(changes, 'maskStartPercentage')) {
            this.updateMask();
        }
        if (this._hasInputChange(changes, 'maskEndPercentage')) {
            this.updateMask();
        }
        if (this._hasInputChange(changes, 'autoFill') ||
            this._hasInputChange(changes, 'useSystemReducedMotion')) {
            this.update();
        }
        if (this._hasInputChange(changes, 'pauseOnHover')) {
            this.updatePauseOnHover();
        }
        if (this._hasInputChange(changes, 'pauseOnClick')) {
            this.updatePauseOnClick();
        }
    }
    /**
     * Check if the marquee need to be updated.
     * @returns True if the marquee need to be updated.
     */
    isMarqueeDirty() {
        return this._hasMarqueeNewItems() || this._hasMarqueeItemsWithChanges();
    }
    /**
     * Update the DOM and the CSS of the marquee.
     */
    update() {
        const { useSystemReducedMotion, autoFill } = this._marqueeComponent;
        if (useSystemReducedMotion &&
            this._reducedMotionService.hasSystemReducedMotion()) {
            this._setAsAnimated(false);
            return;
        }
        this._setAsAnimated(true);
        if (autoFill) {
            this._marqueeDuplicationService.duplicateItems();
            this._updateNumberOfMarqueeItems();
        }
        this.updateMask();
        this.updateMovePercentage();
        this.updateDirection();
        this.updateSpeed();
        this.updatePauseOnHover();
        this.updatePauseOnClick();
        this.updatePlayState();
    }
    /**
     *  Update the speed of the marquee. setting the CSS property '--_animation-duration'.
     */
    updateSpeed() {
        const { speed, marqueeInnerElement, renderer } = this._marqueeComponent;
        if (typeof speed === 'number') {
            const middleSizeInPx = this._getMiddleMarqueeSizeInPx();
            renderer.setStyle(marqueeInnerElement, '--_animation-duration', `${middleSizeInPx / speed}s`, RendererStyleFlags2.DashCase);
            return;
        }
        renderer.setAttribute(marqueeInnerElement, 'data-speed', speed);
    }
    _getMiddleMarqueeSizeInPx() {
        const { marqueeInnerElement, direction } = this._marqueeComponent;
        const marqueeInnerElementSize = marqueeInnerElement.getBoundingClientRect();
        const size = direction === 'left' || direction === 'right'
            ? marqueeInnerElementSize.width
            : marqueeInnerElementSize.height;
        return size / 2;
    }
    /**
     * Update the direction of the marquee. setting the data attribute 'data-direction'.
     */
    updateDirection() {
        const { direction, marqueeElement, renderer } = this._marqueeComponent;
        renderer.setAttribute(marqueeElement, 'data-direction', direction);
    }
    /**
     * Update the percentage of the marquee translation based on the autoFill input.
     * If autoFill is true, the percentage is 50% because the elements will be duplicated
     * until fill the screen (taking into account the animation repetition) and then translated 50%.
     * Otherwise is 100% because the elements will not be duplicated and then translated 100%.
     */
    updateMovePercentage() {
        const { autoFill } = this._marqueeComponent;
        if (!autoFill) {
            this._updateMovePercentage(100);
            return;
        }
        this._updateMovePercentage(50);
    }
    /**
     * Update the mask of the marquee
     */
    updateMask() {
        const { maskEndPercentage, maskStartPercentage, maskPercentage } = this._marqueeComponent;
        if (maskStartPercentage != null || maskEndPercentage != null) {
            this._updateMaskFromStartToEndPercentage();
        }
        if (maskPercentage != null) {
            this._updateMaskFromPercentage();
        }
    }
    /**
     * Update the play state of the marquee animation setting the CSS property '--_animation-play-state'.
     */
    updatePlayState() {
        const { play, marqueeInnerElement, renderer } = this._marqueeComponent;
        renderer.setStyle(marqueeInnerElement, '--_animation-play-state', play ? 'running' : 'paused', RendererStyleFlags2.DashCase);
    }
    /**
     * Update the pause on hover of the marquee setting the data attribute 'data-pause-on-hover'.
     */
    updatePauseOnHover() {
        const { pauseOnHover, renderer, marqueeInnerElement } = this._marqueeComponent;
        renderer.setAttribute(marqueeInnerElement, 'data-pause-on-hover', String(pauseOnHover));
    }
    /**
     * Update the pause on click of the marquee setting the data attribute 'data-pause-on-click'.
     */
    updatePauseOnClick() {
        const { pauseOnClick, renderer, marqueeInnerElement } = this._marqueeComponent;
        renderer.setAttribute(marqueeInnerElement, 'data-pause-on-click', String(pauseOnClick));
    }
    /**
     * Update the percentage of the marquee translation setting the CSS property '--_move-percentage'.
     * @param value - The value of the percentage.
     * @param sign - The sign of the percentage.
     */
    _updateMovePercentage(value, sign = '-') {
        const { marqueeInnerElement, renderer } = this._marqueeComponent;
        renderer.setStyle(marqueeInnerElement, '--_move-percentage', `${sign}${value}%`, RendererStyleFlags2.DashCase);
    }
    /**
     * Update the number of marquee items setting the CSS property '--_number-of-marquee-items'
     * This is done in order to calculate speed from the predefined CSS calculation.
     */
    _updateNumberOfMarqueeItems() {
        const { marqueeInnerElement, renderer, numberOfMarqueeItems } = this._marqueeComponent;
        this._currentNumberOfMarqueeItems = numberOfMarqueeItems;
        renderer.setStyle(marqueeInnerElement, '--_number-of-marquee-items', String(numberOfMarqueeItems), RendererStyleFlags2.DashCase);
    }
    /**
     * Update the mask of the marquee from start to end percentages.
     */
    _updateMaskFromStartToEndPercentage() {
        const { maskStartPercentage, maskEndPercentage, marqueeElement, renderer } = this._marqueeComponent;
        renderer.setStyle(marqueeElement, '--_mask-start-percentage', `${maskStartPercentage}%`, RendererStyleFlags2.DashCase);
        renderer.setStyle(marqueeElement, '--_mask-end-percentage', `${maskEndPercentage}%`, RendererStyleFlags2.DashCase);
    }
    /**
     * Update the mask of the marquee from percentage.
     */
    _updateMaskFromPercentage() {
        const { maskPercentage, marqueeElement, renderer } = this._marqueeComponent;
        renderer.setStyle(marqueeElement, '--_mask-start-percentage', `${maskPercentage}%`, RendererStyleFlags2.DashCase);
        renderer.setStyle(marqueeElement, '--_mask-end-percentage', `${maskPercentage}%`, RendererStyleFlags2.DashCase);
    }
    _hasInputChange(changes, key) {
        return (changes[key] &&
            !changes[key].firstChange &&
            changes[key].currentValue !== changes[key].previousValue);
    }
    /**
     * Check if the marquee has new items inside the marquee inner element.
     * @returns True if the marquee has new items.
     */
    _hasMarqueeNewItems() {
        const { numberOfMarqueeItems } = this._marqueeComponent;
        return this._currentNumberOfMarqueeItems !== numberOfMarqueeItems;
    }
    /**
     * Check if the marquee has items with changes inside the marquee inner element.
     * @returns True if the marquee has items with changes.
     */
    _hasMarqueeItemsWithChanges() {
        const { marqueeItems } = this._marqueeComponent;
        const marqueeItemsArray = Array.from(marqueeItems);
        const originalMarqueeItems = [];
        const totalLenght = marqueeItemsArray.length;
        for (let i = 0; i < totalLenght; i++) {
            const hasAriaHidden = marqueeItemsArray[i].getAttribute('aria-hidden');
            if (!hasAriaHidden) {
                originalMarqueeItems.push(marqueeItemsArray[i]);
                continue;
            }
            break;
        }
        const marqueeItemsChunk = marqueeItemsArray.slice(originalMarqueeItems.length, 2 * originalMarqueeItems.length);
        return originalMarqueeItems.some((item, index) => {
            const marqueeItem = marqueeItemsChunk[index];
            if (!marqueeItem)
                return false;
            return item.innerHTML !== marqueeItem.innerHTML;
        });
    }
    /**
     * Set the marquee as animated or not setting the data attribute 'data-animated'.
     * @param value - True if the marquee is animated, false otherwise.
     */
    _setAsAnimated(value) {
        const { renderer, marqueeElement } = this._marqueeComponent;
        renderer.setAttribute(marqueeElement, 'data-animated', String(value));
    }
}
MarqueeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: MarqueeService, deps: [{ token: ReducedMotionService }, { token: MarqueeDuplicationService }], target: i0.ɵɵFactoryTarget.Injectable });
MarqueeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: MarqueeService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: MarqueeService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ReducedMotionService }, { type: MarqueeDuplicationService }]; } });

class NgxFastMarqueeComponent {
    constructor(_hostRef, renderer, _marqueeService, _ngZone) {
        this._hostRef = _hostRef;
        this.renderer = renderer;
        this._marqueeService = _marqueeService;
        this._ngZone = _ngZone;
        /**
         * Direction of the marquee.
         * @default 'left'
         */
        this.direction = 'left';
        /**
         * Speed of the marquee.
         * Can be qualitative as 'slow', 'medium' or 'fast' or quantitative as a number in pixels per second.
         * The quantitative speed is calculated based on the number of the marquee items.
         * @default 'medium'
         */
        this.speed = 'medium';
        /**
         * Whether to have into account the system reduced motion.
         * If true, the marquee will not be animated when the system has reduced motion.
         * @default false
         */
        this.useSystemReducedMotion = false;
        /**
         * Whether to fill the marquee with duplicated items.
         * If true, the marquee will be filled with duplicated items.
         * @default true
         */
        this.autoFill = true;
        /**
         * Whether to play the marquee.
         * True to play the marquee animation, false to pause the marquee animation.
         * @default true
         */
        this.play = true;
        /**
         * Whether to pause the marquee when the mouse is over the marquee.
         * @default false
         */
        this.pauseOnHover = false;
        /**
         * Whether to pause the marquee when the mouse is clicked over the marquee.
         */
        this.pauseOnClick = false;
        /**
         * Event emitted when the marquee is mounted in the view.
         * Emitted once.
         */
        this.mounted = new EventEmitter();
        /**
         * Event emitted when the marquee is mounted updated.
         * Emitted each time the marquee is updated.
         */
        this.updated = new EventEmitter();
        /**
         * Flag to check if the window is being resized. True if the window is being resized, false otherwise.
         */
        this._isOnResizing = false;
    }
    /**
     * HTML Marquee Element.
     */
    get marqueeElement() {
        return this._hostRef.nativeElement;
    }
    /**
     * HTML Marquee Inner Element.
     */
    get marqueeInnerElement() {
        return this.marqueeInnerRef.nativeElement;
    }
    /**
     * Number of children inside the marquee inner element.
     */
    get numberOfMarqueeItems() {
        return this.marqueeInnerElement.children.length;
    }
    /**
     * Children inside the marquee inner element.
     */
    get marqueeItems() {
        return this.marqueeInnerElement.children;
    }
    ngOnChanges(changes) {
        this._marqueeService.updateOnInputChanges(changes);
    }
    ngAfterContentInit() {
        this._marqueeService.setMarqueeComponent(this);
        this.mounted.emit();
    }
    ngAfterContentChecked() {
        if (this._marqueeService.isMarqueeDirty()) {
            this._startMarqueeUpdeting();
        }
    }
    /**
     * Update the marquee when the window is resized.
     */
    _onResize() {
        this._isOnResizing = true;
        this._ngZone.runOutsideAngular(() => {
            if (this._preventUpdateOnResizingTimeout) {
                clearTimeout(this._preventUpdateOnResizingTimeout);
            }
            this._preventUpdateOnResizingTimeout = setTimeout(() => {
                this._isOnResizing = false;
                this._startMarqueeUpdeting();
            }, 50);
        });
    }
    /**
     * Start the update of the marquee.
     */
    _startMarqueeUpdeting() {
        if (this._isOnResizing)
            return;
        // TODO: Check why is needed to use a timeout here. Otherwise, the marquee is not updated at the first time.
        this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                this._updateMarqueee();
            }, 0);
        });
    }
    _updateMarqueee() {
        this._marqueeService.update();
        this.updated.emit();
    }
}
NgxFastMarqueeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: NgxFastMarqueeComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: MarqueeService }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
NgxFastMarqueeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.0.5", type: NgxFastMarqueeComponent, selector: "ngx-fast-marquee", inputs: { direction: "direction", speed: "speed", useSystemReducedMotion: "useSystemReducedMotion", autoFill: "autoFill", maskStartPercentage: "maskStartPercentage", maskEndPercentage: "maskEndPercentage", maskPercentage: "maskPercentage", play: "play", pauseOnHover: "pauseOnHover", pauseOnClick: "pauseOnClick" }, outputs: { mounted: "mounted", updated: "updated" }, host: { listeners: { "window:resize": "_onResize()" } }, providers: [MarqueeService, MarqueeDuplicationService], viewQueries: [{ propertyName: "marqueeInnerRef", first: true, predicate: ["marqueeInner"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div #marqueeInner class=\"ngx-fast-marquee__inner\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host{display:block;max-width:100%;max-height:100%;--_mask-start-percentage: 0%;--_mask-end-percentage: 0%;--_mask-direction: 90deg}:host .ngx-fast-marquee__inner{display:flex;flex-wrap:wrap}:host[data-animated=true]{overflow:hidden;--_linear-gradient: linear-gradient( var(--_mask-direction), transparent, white calc(var(--_mask-start-percentage) * .5), white calc(100% - var(--_mask-end-percentage) * .5), transparent );-webkit-mask:var(--_linear-gradient);mask:var(--_linear-gradient)}:host[data-animated=true][data-direction=left],:host[data-animated=true][data-direction=right]{--_mask-direction: 90deg;width:100%;height:auto}:host[data-animated=true][data-direction=left] .ngx-fast-marquee__inner,:host[data-animated=true][data-direction=right] .ngx-fast-marquee__inner{flex-direction:row;--_translation: translateX(var(--_move-percentage))}:host[data-animated=true][data-direction=up],:host[data-animated=true][data-direction=down]{--_mask-direction: 180deg;width:auto;height:100%}:host[data-animated=true][data-direction=up] .ngx-fast-marquee__inner,:host[data-animated=true][data-direction=down] .ngx-fast-marquee__inner{flex-direction:column;--_translation: translateY(var(--_move-percentage))}:host[data-animated=true][data-direction=right],:host[data-animated=true][data-direction=down]{--_animation-direction: reverse}:host[data-animated=true][data-direction=left],:host[data-animated=true][data-direction=up]{--_animation-direction: normal}:host[data-animated=true] .ngx-fast-marquee__inner{--_move-percentage: -50%;--_number-of-marquee-items: 0;--_animation-duration: 0s;--_animation-iteration-count: infinite;--_animation-play-state: running;width:max-content;flex-wrap:nowrap;animation-name:move;animation-duration:var(--_animation-duration);animation-direction:var(--_animation-direction);animation-fill-mode:forwards;animation-timing-function:linear;animation-iteration-count:var(--_animation-iteration-count);animation-play-state:var(--_animation-play-state)}:host[data-animated=true] .ngx-fast-marquee__inner[data-speed=fast]{--_animation-duration: calc(var(--_number-of-marquee-items) * .5s)}:host[data-animated=true] .ngx-fast-marquee__inner[data-speed=medium]{--_animation-duration: calc(var(--_number-of-marquee-items) * 5s)}:host[data-animated=true] .ngx-fast-marquee__inner[data-speed=slow]{--_animation-duration: calc(var(--_number-of-marquee-items) * 10s)}:host[data-animated=true] .ngx-fast-marquee__inner[data-state=running]{--_animation-play-state: running}:host[data-animated=true] .ngx-fast-marquee__inner[data-state=paused]{--_animation-play-state: paused}:host[data-animated=true] .ngx-fast-marquee__inner[data-pause-on-hover=true]:hover{--_animation-play-state: paused !important}:host[data-animated=true] .ngx-fast-marquee__inner[data-pause-on-click=true]:active{--_animation-play-state: paused !important}@keyframes move{to{transform:var(--_translation)}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: NgxFastMarqueeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ngx-fast-marquee',
                    templateUrl: './ngx-fast-marquee.component.html',
                    styleUrls: ['./ngx-fast-marquee.component.scss'],
                    providers: [MarqueeService, MarqueeDuplicationService],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: MarqueeService }, { type: i0.NgZone }]; }, propDecorators: { marqueeInnerRef: [{
                type: ViewChild,
                args: ['marqueeInner', { static: true }]
            }], direction: [{
                type: Input
            }], speed: [{
                type: Input
            }], useSystemReducedMotion: [{
                type: Input
            }], autoFill: [{
                type: Input
            }], maskStartPercentage: [{
                type: Input
            }], maskEndPercentage: [{
                type: Input
            }], maskPercentage: [{
                type: Input
            }], play: [{
                type: Input
            }], pauseOnHover: [{
                type: Input
            }], pauseOnClick: [{
                type: Input
            }], mounted: [{
                type: Output
            }], updated: [{
                type: Output
            }], _onResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class NgxFastMarqueeModule {
}
NgxFastMarqueeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: NgxFastMarqueeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxFastMarqueeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: NgxFastMarqueeModule, declarations: [NgxFastMarqueeComponent], imports: [CommonModule], exports: [NgxFastMarqueeComponent] });
NgxFastMarqueeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: NgxFastMarqueeModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.0.5", ngImport: i0, type: NgxFastMarqueeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [NgxFastMarqueeComponent],
                    imports: [CommonModule],
                    exports: [NgxFastMarqueeComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NgxFastMarqueeComponent, NgxFastMarqueeModule };
